import { AppStateDto, DEFAULT_PAGE_LAYOUT, PageLayout, PageLayoutContent } from './app.model';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { catchError, tap } from 'rxjs/operators';
import { CustomerService } from './proxy/customers/customer.service';
import {
  GetUserProfile,
  GetCustomer,
  GetVendor,
  GetUserAndGroups,
  GetPageLayout,
  SavePageLayout,
  ResetPageLayout,
} from './app.actions';
import { UserProfileDto } from './proxy/users/models';
import { UserProfileService } from './proxy/users/user-profile.service';
import { CustomerDto } from './proxy/customers/models';

import { ConfigStateService, ApplicationConfigurationDto } from '@abp/ng.core';
import { EMPTY, Observable, Observer } from 'rxjs';
import { VendorDto } from './proxy/vendors/models';
import { VendorService } from '@proxy/vendors';
import { PageRequestDto } from '@proxy/models';
import { EntityContentDto, PageLayoutService } from '@proxy/common';

@State<AppStateDto>({
  name: 'AppState',
  defaults: {
    customer: null,
    vendor: null,
    userProfile: null,
    usersAndGroups: null,
    pageLayoutContent: undefined,
  } as AppStateDto,
})
@Injectable()
export class AppState {
  @Selector()
  static getUserProfile({ userProfile }: AppStateDto): UserProfileDto {
    return userProfile;
  }

  @Selector()
  static getCustomer({ customer }: AppStateDto): CustomerDto[] {
    return customer;
  }

  @Selector()
  static getVendor({ vendor }: AppStateDto): VendorDto[] {
    return vendor;
  }

  @Selector()
  static getUsersAndGroups({ usersAndGroups }: AppStateDto): UserProfileDto[] {
    return usersAndGroups;
  }

  @Selector()
  static getPageLayoutContent({ pageLayoutContent }: AppStateDto): EntityContentDto {
    return pageLayoutContent;
  }

  constructor(
    private userProfileService: UserProfileService,
    private customerService: CustomerService,
    private vendorService: VendorService,
    private pageLayoutService: PageLayoutService,
    private configService: ConfigStateService,
    private store: Store
  ) { }

  @Action(GetUserProfile)
  getUserProfile({ patchState }: StateContext<AppStateDto>, { }: GetUserProfile) {
    const currentUser = this.configService.getOne(
      'currentUser'
    ) as ApplicationConfigurationDto['currentUser'];

    if (!currentUser?.id) {
      console.error('Current user not found or missing ID');
      return new Observable(observer => {
        observer.error('Current user not available');
      });
    }

    return this.userProfileService.getByUserId(currentUser.id).pipe(
      tap(result => {
        return patchState({
          userProfile: result,
        });
      })
    );
  }

  @Action(GetCustomer)
  getCustomer({ patchState }: StateContext<AppStateDto>, { payload }: GetCustomer) {
    const storeSnapShot = this.store.snapshot();
    const customer = storeSnapShot?.AppState?.userProfile?.customer;
    if (customer) {
      return new Observable<CustomerDto[]>((observer: Observer<CustomerDto[]>) => {
        observer.next([customer]);
        observer.complete();
        return { unsubscribe() { } };
      }).pipe(
        tap(result => {
          return patchState({
            customer: result,
          });
        })
      );
    } else {
      return this.customerService.getExpandById(payload).pipe(
        tap(result => {
          return patchState({
            customer: result,
          });
        })
      );
    }
  }

  @Action(GetVendor)
  getVendor({ patchState }: StateContext<AppStateDto>, { payload }: GetVendor) {
    const storeSnapShot = this.store.snapshot();
    const vendor = storeSnapShot?.AppState?.userProfile?.vendor;
    if (vendor) {
      return new Observable<VendorDto[]>((observer: Observer<VendorDto[]>) => {
        observer.next([vendor]);
        observer.complete();
        return { unsubscribe() { } };
      }).pipe(
        tap(result => {
          return patchState({
            vendor: result,
          });
        })
      );
    } else {
      return this.vendorService.getExpandById(payload).pipe(
        tap(result => {
          return patchState({
            vendor: result,
          });
        })
      );
    }
  }

  @Action(GetUserAndGroups)
  getUsersAndGroups({ patchState }: StateContext<AppStateDto>, { }: GetUserAndGroups) {
    return this.userProfileService.getList({ maxResultCount: 250 } as PageRequestDto).pipe(
      tap(result => {
        return patchState({
          usersAndGroups: result.items,
        });
      })
    );
  }

  // pageLayoutContent: undefined = not loaded yet (list pages wait), null = user has no saved layout (defaults).
  // It is persisted to localStorage (app.module) so a hard refresh doesn't flash the default layout.
  @Action(GetPageLayout)
  getPageLayout({ getState, patchState }: StateContext<AppStateDto>, { }: GetPageLayout) {
    const currentUser = this.configService.getOne('currentUser') as ApplicationConfigurationDto['currentUser'];

    // Not signed in yet (e.g. /signin-callback before the token exchange); the callback dispatches again.
    if (!currentUser?.id) {
      patchState({ pageLayoutContent: undefined });
      return;
    }

    // The persisted value may belong to whoever used this browser before.
    const cached = getState().pageLayoutContent;
    if (cached && cached.entityId !== currentUser.id) {
      patchState({ pageLayoutContent: undefined });
    }

    return this.pageLayoutService.get().pipe(
      tap(result => patchState({ pageLayoutContent: toPageLayoutContent(result) })),
      catchError(error => {
        console.error('Error loading page layout:', error);
        // Never leave list pages waiting on a failed request.
        if (getState().pageLayoutContent === undefined) patchState({ pageLayoutContent: null });
        return EMPTY;
      })
    );
  }

  @Action(SavePageLayout)
  savePageLayout({ patchState }: StateContext<AppStateDto>, { payload }: SavePageLayout) {
    return this.pageLayoutService
      .update({ ...normalizePageLayout(payload) })
      .pipe(tap(result => patchState({ pageLayoutContent: toPageLayoutContent(result) })));
  }

  @Action(ResetPageLayout)
  resetPageLayout({ patchState }: StateContext<AppStateDto>, { }: ResetPageLayout) {
    return this.pageLayoutService.delete().pipe(tap(() => patchState({ pageLayoutContent: null })));
  }
}

function normalizePageLayout(layout: Partial<PageLayout> | null | undefined): PageLayout {
  const merged = { ...DEFAULT_PAGE_LAYOUT, ...(layout ?? {}) };
  return {
    ...merged,
    // Older records stored tableView as a number
    tableView: String(merged.tableView ?? DEFAULT_PAGE_LAYOUT.tableView) as PageLayout['tableView'],
    recordsPerPage: Number(merged.recordsPerPage) || DEFAULT_PAGE_LAYOUT.recordsPerPage,
  };
}

function toPageLayoutContent(dto: EntityContentDto | null | undefined): PageLayoutContent | null {
  if (!dto?.id) return null;

  let parsed: Partial<PageLayout> = {};
  try {
    parsed = dto.content ? JSON.parse(dto.content) : {};
  } catch {
    console.error('Invalid page layout content, using defaults:', dto.content);
  }
  return { ...dto, pageLayout: normalizePageLayout(parsed) };
}
